// Block Map
//   - desktop: draw an SVG connector line from each #pin-{i} (inside the inlined
//     background SVG) to its button, and open a modal on click.
//   - mobile: the map above an accordion; opening an item highlights the
//     matching #area-{i} in the mobile copy of the inlined SVG.
// The connector lines are recomputed on resize and after fonts/layout settle.

const SVG_NS = "http://www.w3.org/2000/svg";

// Vertical gap between the lowest label tier and the top of the map, the gap
// between stacked tiers, and the horizontal gap kept between two labels that
// share a tier — all in px.
const BAND_GAP = 24;
const TIER_GAP = 12;
const COL_GAP = 16;

/* --------------------- Label placement + connector lines ------------------ */
// Labels sit in a band ABOVE the map (never on it). Each label is aligned
// horizontally over its pin; labels that would overlap are stacked onto higher
// tiers. The band's height is sized to fit every tier, then a connector line is
// drawn from each label down to its pin.

function layout(stage) {
    const canvas = stage.querySelector("[data-map-canvas]");
    const overlay = stage.querySelector("[data-map-lines]");
    const band = stage.querySelector("[data-map-buttons]");
    if (!canvas || !overlay || !band) return;

    // Collapse the band first so pin/stage measurements are not skewed by a
    // band height left over from a previous run.
    band.style.height = "0px";

    let stageRect = stage.getBoundingClientRect();
    if (!stageRect.width) return;

    // Pass A — natural size, icon offset and pin position (band collapsed, so
    // pin Y reflects the pin's depth on the plan).
    const items = [];
    stage.querySelectorAll("[data-map-btn]").forEach((btn) => {
        const pin = canvas.querySelector("#" + CSS.escape(btn.dataset.pin));
        if (!pin) return;

        const pinRect = pin.getBoundingClientRect();
        const pinX = pinRect.left + pinRect.width / 2 - stageRect.left;
        const pinY = pinRect.top + pinRect.height / 2 - stageRect.top;

        btn.style.left = "0px";
        btn.style.top = "0px";
        const icon = btn.querySelector(".block-map__btn-icon");
        const iconCenter = icon ? icon.offsetLeft + icon.offsetWidth / 2 : 0;

        items.push({ btn, pin, pinX, pinY, w: btn.offsetWidth, h: btn.offsetHeight, iconCenter });
    });

    if (!items.length) return;

    const rowStep = Math.max(...items.map((i) => i.h)) + TIER_GAP;

    // Base tier from pin depth: pins further back (smaller Y) get higher
    // labels, so heights stagger with the floorplan's perspective.
    const ys = items.map((i) => i.pinY);
    const minY = Math.min(...ys);
    const span = Math.max(1, Math.max(...ys) - minY);
    const depthTiers = Math.min(items.length, 5);
    items.forEach((it) => {
        const frac = (Math.max(...ys) - it.pinY) / span; // 0 front … 1 back
        it.tier = Math.round(frac * (depthTiers - 1));
    });

    // Resolve horizontal collisions: process left-to-right and bump a label up
    // a tier if it would overlap one already placed on the same tier.
    items.sort((a, b) => a.pinX - b.pinX);
    const tiers = []; // tiers[t] = array of [xStart, xEnd] already occupied
    let maxTier = 0;

    items.forEach((it) => {
        // Left so the icon centre lands over the pin, clamped to the stage.
        let left = it.pinX - it.iconCenter;
        left = Math.max(0, Math.min(left, stageRect.width - it.w));
        it.left = left;
        const range = [left, left + it.w];

        const hits = (t) =>
            tiers[t] &&
            tiers[t].some((r) => !(range[1] + COL_GAP < r[0] || range[0] > r[1] + COL_GAP));
        while (hits(it.tier)) it.tier++;

        (tiers[it.tier] = tiers[it.tier] || []).push(range);
        if (it.tier > maxTier) maxTier = it.tier;
    });

    // Size the band to fit every tier and place each label inside it (tier 0
    // bottom sits BAND_GAP above the map).
    const bandHeight = (maxTier + 1) * rowStep + BAND_GAP;
    band.style.height = bandHeight + "px";
    items.forEach((it) => {
        it.btn.style.left = it.left + "px";
        it.btn.style.top = bandHeight - BAND_GAP - (it.tier + 1) * rowStep + "px";
    });

    // Pass B — draw the lines now the band has pushed the map to its final
    // position; re-measure against the grown stage.
    stageRect = stage.getBoundingClientRect();
    overlay.setAttribute("width", stageRect.width);
    overlay.setAttribute("height", stageRect.height);
    overlay.setAttribute("viewBox", `0 0 ${stageRect.width} ${stageRect.height}`);
    overlay.innerHTML = "";

    items.forEach((it) => {
        const pinRect = it.pin.getBoundingClientRect();
        const x2 = pinRect.left + pinRect.width / 2 - stageRect.left;
        const y2 = pinRect.top + pinRect.height / 2 - stageRect.top;

        const icon = it.btn.querySelector(".block-map__btn-icon") || it.btn;
        const iconRect = icon.getBoundingClientRect();
        const btnRect = it.btn.getBoundingClientRect();
        const x1 = iconRect.left + iconRect.width / 2 - stageRect.left;
        const y1 = btnRect.bottom - stageRect.top;

        const line = document.createElementNS(SVG_NS, "line");
        line.setAttribute("x1", x1);
        line.setAttribute("y1", y1);
        line.setAttribute("x2", x2);
        line.setAttribute("y2", y2);
        line.setAttribute("class", "block-map__line");
        overlay.appendChild(line);
    });
}

/* --------------------------------- Modals --------------------------------- */

// Swiper can't measure a hidden element, so the gallery is initialised on the
// first open and refreshed on every open.
function initModalSwiper(modal) {
    const el = modal.querySelector("[data-map-swiper]");
    if (!el || !window.Swiper) return;

    if (el._swiper) {
        el._swiper.update();
        return;
    }

    el._swiper = new window.Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 16,
        pagination: {
            el: el.querySelector(".swiper-pagination"),
            clickable: true,
        },
    });
}

function openModal(modal) {
    modal.hidden = false;
    document.body.classList.add("block-map-modal-open");
    requestAnimationFrame(() => {
        modal.classList.add("is-open");
        initModalSwiper(modal);
    });
    const close = modal.querySelector("[data-map-close]");
    if (close) close.focus();
}

function closeModal(modal) {
    modal.classList.remove("is-open");
    document.body.classList.remove("block-map-modal-open");
    const onEnd = () => {
        modal.hidden = true;
        modal.removeEventListener("transitionend", onEnd);
    };
    modal.addEventListener("transitionend", onEnd);
    // Fallback if no transition fires.
    setTimeout(() => { if (!modal.classList.contains("is-open")) modal.hidden = true; }, 400);
}

function initModals(block) {
    block.querySelectorAll("[data-map-btn]").forEach((btn) => {
        const modal = block.querySelector("#" + CSS.escape(btn.dataset.target));
        if (!modal) return;

        // Portal the modal to <body> so no ancestor stacking context can trap
        // it beneath the fixed header / bottom nav. It keeps its own listeners
        // (they're attached to nodes inside the modal, which move with it).
        if (modal.parentNode !== document.body) document.body.appendChild(modal);

        btn.addEventListener("click", () => openModal(modal));

        // The matching map area (#area-{i} ↔ data-pin="pin-{i}") opens the same
        // modal. Scope the lookup to this stage so duplicate IDs in another
        // panel's inlined SVG don't collide.
        const stage = btn.closest("[data-map-stage]");
        const areaId = btn.dataset.pin.replace(/^pin-/, "area-");
        const area = stage && stage.querySelector("#" + CSS.escape(areaId));
        if (area) area.addEventListener("click", () => openModal(modal));

        modal.querySelectorAll("[data-map-close]").forEach((el) =>
            el.addEventListener("click", () => closeModal(modal))
        );
    });

    document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        block.querySelectorAll("[data-map-modal].is-open").forEach(closeModal);
    });
}

/* ------------------------------- Accordion -------------------------------- */

// The mobile map is the same SVG inlined again, so an accordion item and its
// map area pair up by index (data-pin="pin-{i}" ↔ #area-{i}), mirroring the
// desktop hover pairing. Lookups stay scoped to the panel's own mobile canvas,
// since every panel inlines a background using the same ids.
function mobileArea(item) {
    const mobile = item.closest(".block-map__mobile");
    const canvas = mobile && mobile.querySelector("[data-map-mobile-canvas]");
    if (!canvas || !item.dataset.pin) return null;
    return canvas.querySelector("#" + CSS.escape(item.dataset.pin.replace(/^pin-/, "area-")));
}

function initAccordion(block) {
    const items = block.querySelectorAll("[data-acc-item]");
    items.forEach((item) => {
        const toggle = item.querySelector("[data-acc-toggle]");
        const panel = item.querySelector("[data-acc-panel]");
        if (!toggle || !panel) return;

        const open = () => {
            const isOpen = item.classList.contains("is-open");
            items.forEach((other) => {
                other.classList.remove("is-open");
                const t = other.querySelector("[data-acc-toggle]");
                const p = other.querySelector("[data-acc-panel]");
                const a = mobileArea(other);
                if (t) t.setAttribute("aria-expanded", "false");
                if (p) p.hidden = true;
                if (a) a.classList.remove("is-active");
            });
            if (!isOpen) {
                item.classList.add("is-open");
                toggle.setAttribute("aria-expanded", "true");
                panel.hidden = false;
                const area = mobileArea(item);
                if (area) area.classList.add("is-active");
            }
        };

        toggle.addEventListener("click", open);

        // Tapping the area on the map opens (or closes) its accordion item.
        const area = mobileArea(item);
        if (area) area.addEventListener("click", open);
    });
}

/* ----------------------------------- Tabs --------------------------------- */
// Each map lives in its own panel; tabs toggle which panel is visible. A hidden
// panel has zero-size stages, so its layout() runs as a no-op — the panel is
// re-laid-out the moment its tab is activated (and on resize while visible).

function initTabs(block, relayout) {
    const tabs = Array.from(block.querySelectorAll("[data-map-tab]"));
    if (!tabs.length) return;

    tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
            const panelId = tab.dataset.target;

            tabs.forEach((t) => {
                const active = t === tab;
                t.classList.toggle("is-active", active);
                t.setAttribute("aria-selected", active ? "true" : "false");
            });

            block.querySelectorAll("[data-map-panel]").forEach((panel) => {
                const active = panel.id === panelId;
                panel.classList.toggle("is-active", active);
                panel.hidden = !active;
                // Lines/labels can only be measured once the panel is visible.
                if (active) relayout();
            });
        });
    });
}

/* ---------------------------------- Init ---------------------------------- */

function initBlockMap(block) {
    initModals(block);
    initAccordion(block);

    // Lay out every currently-visible stage (hidden panels no-op until shown).
    const redraw = () =>
        block.querySelectorAll("[data-map-stage]").forEach(layout);

    initTabs(block, redraw);

    redraw();
    // Re-run once layout/fonts settle and on resize.
    window.addEventListener("load", redraw);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(redraw);

    let raf = null;
    window.addEventListener("resize", () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(redraw);
    });
}

function initAll() {
    document.querySelectorAll("[data-block-map]").forEach(initBlockMap);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
} else {
    initAll();
}
